/**
 * Prerequisite discovery, replacing `GET /api/health` and lib/health.ts.
 *
 * The agent build had three things to install on the event PC — the agent, the
 * print helper and FFmpeg — and a full-screen `SetupScreen` existed to name
 * whichever was missing, because the desktop app discovered them at the point
 * of use and told the operator only after a print had already gone out.
 *
 * There is nothing to install now, and that screen is gone: it stood between
 * the operator and the console every single evening, since Chrome downgrades a
 * stored directory handle to "prompt" across a browser restart. The console
 * opens straight onto the flow, and what is missing is reported where it is
 * needed — a banner that leads to Settings, and a disabled "Mulai pantau".
 *
 * What survived the move is the *checking*, not the interstitial. Everything
 * here is still probed up front rather than at the point of use, because
 * finding out mid-session is what this whole module exists to prevent.
 */

import { probeVideoSupport, type VideoSupport } from '@/app/lib/video'
import { MONITORED_HANDLE, TARGET_HANDLE, checkPermission, loadHandle, supported } from './local/fs'
import { raiaConfigured, type Settings } from './local/settings'

export type GrantState = 'missing' | 'prompt' | 'granted'

export interface Capabilities {
  /** File System Access. Without it there is no way to see the camera folder. */
  fileSystem: boolean
  /** The camera folder: chosen, and still permitted this session. */
  monitored: GrantState
  /** The archive folder, where the local copy of each session is filed. */
  target: GrantState
  /** Whether animate.mp4 can really be encoded here. See lib/video.ts. */
  video: VideoSupport
  /** raia-api credentials are set, so sessions can be uploaded. */
  upload: boolean
  /** At least one layout exists. Without one there is nothing to composite onto. */
  layouts: boolean
}

async function grantState(key: string): Promise<GrantState> {
  const handle = await loadHandle(key)
  if (!handle) return 'missing'
  return (await checkPermission(handle, key === TARGET_HANDLE ? 'readwrite' : 'read')) === 'granted'
    ? 'granted'
    : 'prompt'
}

export async function probe(settings: Settings | null, layoutCount: number): Promise<Capabilities> {
  // Cheap now: there is no platform encoder to interrogate, only WebAssembly to
  // have and a 32MB core to already hold. It used to configure a real
  // VideoEncoder and push a frame through it, because `isConfigSupported()`
  // could not be trusted — see lib/video.ts for why that whole path went away.
  const video = await probeVideoSupport()

  if (!supported()) {
    return {
      fileSystem: false,
      monitored: 'missing',
      target: 'missing',
      video,
      upload: raiaConfigured(settings),
      layouts: layoutCount > 0,
    }
  }

  return {
    fileSystem: true,
    monitored: await grantState(MONITORED_HANDLE),
    target: await grantState(TARGET_HANDLE),
    video,
    upload: raiaConfigured(settings),
    layouts: layoutCount > 0,
  }
}

export interface Requirement {
  key: 'browser' | 'monitored' | 'target' | 'layouts' | 'upload' | 'encoder'
  /** Names the Settings card that fixes it, so the banner and the screen agree. */
  label: string
  /** One line, in the banner. Says what is wrong, not how the API works. */
  detail: string
}

/**
 * What is still missing before the console may be used.
 *
 * Every one of these is a hard requirement. That is a deliberate change: the
 * archive folder, the layouts, the Raia account and the encoder were all once
 * "optional", on the reasoning that a guest still gets a printed sheet with a
 * working QR without them. True, and it made for a console that could be picked
 * up half-configured and only reveal it later — a session with no archive copy,
 * a link that never resolves, a first guest waiting on a 32MB download. There
 * is no cost to requiring all of it: setup happens once, before the doors open,
 * and every one of these is fixed from Settings in under a minute.
 *
 * The encoder tracks `videoEnabled`. An operator who has deliberately turned
 * the animation off has said they do not want that file, and holding the whole
 * console hostage to a download for a feature nobody is using would be a trap
 * with no way out of it.
 */
export function missingRequirements(
  capabilities: Capabilities,
  videoEnabled: boolean,
): Requirement[] {
  // The browser itself fails first and alone: on Firefox or Safari there is no
  // folder to grant and no point listing five more things the operator cannot
  // act on. Chromium is the fix, and it is the only one.
  if (!capabilities.fileSystem) {
    return [
      {
        key: 'browser',
        label: 'Browser',
        detail: 'Butuh Google Chrome atau Microsoft Edge — browser ini tidak bisa membaca folder',
      },
    ]
  }

  const missing: Requirement[] = []

  if (capabilities.monitored !== 'granted') {
    missing.push({
      key: 'monitored',
      label: 'Sumber foto',
      detail:
        capabilities.monitored === 'prompt'
          ? 'Sumber foto perlu diizinkan lagi setelah browser ditutup'
          : 'Folder kamera belum dipilih',
    })
  }

  if (capabilities.target !== 'granted') {
    missing.push({
      key: 'target',
      label: 'Arsip lokal',
      detail:
        capabilities.target === 'prompt'
          ? 'Arsip lokal perlu diizinkan lagi setelah browser ditutup'
          : 'Folder arsip belum dipilih',
    })
  }

  if (!capabilities.layouts) {
    missing.push({ key: 'layouts', label: 'Layout', detail: 'Belum ada layout untuk dicetak' })
  }

  if (!capabilities.upload) {
    missing.push({ key: 'upload', label: 'Akun Raia', detail: 'Akun Raia belum diatur' })
  }

  if (videoEnabled) {
    if (!capabilities.video.supported) {
      missing.push({
        key: 'encoder',
        label: 'Video sesi',
        detail: capabilities.video.reason ?? 'Encoder video tidak tersedia di PC ini',
      })
    } else if (capabilities.video.needsDownload) {
      missing.push({
        key: 'encoder',
        label: 'Video sesi',
        detail: 'Encoder video belum diunduh (32 MB, sekali saja per PC)',
      })
    }
  }

  return missing
}

/** Whether the console is fully set up and the operator may start watching. */
export function ready(capabilities: Capabilities | null, videoEnabled: boolean): boolean {
  return capabilities !== null && missingRequirements(capabilities, videoEnabled).length === 0
}
